import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { GraduationCap, AlertTriangle } from 'lucide-react';
import { api } from '../api';
import ChipEstado from '../ui/ChipEstado';
import NotaExplicativa from '../ui/NotaExplicativa';
import EstadoVacio from '../ui/EstadoVacio';
import {
  estadoHabilidad, comoVaLaOperacion, NIVELES, AVISO_DC3_SIN_VERIFICAR,
} from '../ui/competencias';

// MATRIZ DE HABILIDADES · Fase 4 del despegue.
//
// Contesta una sola pregunta, y por eso no es una tabla más: «¿quién
// puede hacer esto HOY?». Lo que la hace útil es que una habilidad
// vencida —que en cualquier lista se ve igual que una vigente— aquí deja
// la operación entera marcada.
export default function Competencias() {
  const [matriz, setMatriz] = useState(null);
  const [vencimientos, setVencimientos] = useState([]);
  const [error, setError] = useState('');
  const [abierta, setAbierta] = useState(null);
  useSubpantalla('competencia', !!abierta, () => setAbierta(null), { valor: abierta, abrir: setAbierta, reemplaza: true });

  const cargar = useCallback(() => {
    api.get('/competencias/matriz').then(setMatriz)
      .catch((e) => setError(e.message));
    api.get('/competencias/vencimientos').then(setVencimientos).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  if (error) {
    return (
      <div className="m-4 flex gap-2 rounded-xl border border-red-200
                      bg-red-50 p-3 text-sm text-red-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }
  if (!matriz) {
    return <p className="p-4 text-sm text-gray-500">Cargando la matriz…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">
          Quién puede hacer qué
        </h1>
        <p className="text-sm text-gray-600">
          Las operaciones de tu planta, lo que exige cada una y quién está
          autorizado hoy.
        </p>
      </header>

      <NotaExplicativa titulo="Por qué «hoy» está en negritas">
        Una autorización vencida <strong>se ve igual que una vigente</strong>
        {' '}en cualquier lista, y quien reparte el trabajo por la mañana no
        va a revisar fechas una por una. Aquí, si a una operación le falta
        alguien vigente para algo que exige, la operación entera sale
        marcada — aunque haya diez personas con esa habilidad vencida.
      </NotaExplicativa>

      <p className="text-sm font-semibold text-gray-700">{matriz.resumen}</p>

      {matriz.operaciones.length === 0 ? (
        <EstadoVacio
          icono={GraduationCap}
          titulo="Todavía no has dado de alta ninguna operación"
          descripcion="Empieza por las tres o cuatro que de verdad exigen certificación —soldar, inspeccionar, manejar el montacargas—. Con eso ya puedes contestar «¿quién puede hacer esto hoy?» sin preguntarle a nadie."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {matriz.operaciones.map((op) => (
            <li key={op.id}
                className={`rounded-xl border p-4 ${
                  op.sin_exigencias ? 'border-gray-200'
                    : op.cubierta ? 'border-green-300 bg-green-50/40'
                    : 'border-red-300 bg-red-50/40'}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-gray-900">
                    <span className="font-mono text-sm text-gray-500">
                      {op.codigo}
                    </span>{' '}
                    {op.nombre}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {comoVaLaOperacion(op)}
                  </p>
                </div>
                <button type="button"
                        onClick={() => setAbierta(abierta === op.id ? null : op.id)}
                        className="shrink-0 text-sm font-semibold
                                   text-[var(--color-marca)]">
                  {abierta === op.id ? 'Ocultar' : 'Ver quién'}
                </button>
              </div>

              {abierta === op.id && (
                <div className="mt-3 flex flex-col gap-3">
                  {op.habilidades.length === 0 && (
                    <p className="text-sm text-gray-500">
                      No exige ninguna habilidad.
                    </p>
                  )}
                  {op.habilidades.map((h) => (
                    <div key={h.id_habilidad}>
                      <p className="text-sm font-semibold text-gray-800">
                        {h.nombre}
                        {h.vigencia_meses
                          ? <span className="ml-2 text-xs font-normal text-gray-500">
                              se renueva cada {h.vigencia_meses} meses
                            </span>
                          : <span className="ml-2 text-xs font-normal text-gray-500">
                              no caduca
                            </span>}
                      </p>
                      {h.personas.length === 0 ? (
                        <p className="text-sm text-red-700">
                          Nadie tiene esta habilidad.
                        </p>
                      ) : (
                        <ul className="mt-1 flex flex-col gap-1">
                          {h.personas.map((p) => (
                            <li key={p.id}
                                className="flex items-center justify-between gap-3 text-sm">
                              <span className="text-gray-800">
                                {p.persona}
                                <span className="ml-2 text-xs text-gray-500">
                                  {NIVELES[p.nivel] || p.nivel}
                                </span>
                              </span>
                              <span className="flex items-center gap-2">
                                {p.vigente_hasta && (
                                  <span className="text-xs text-gray-500">
                                    hasta el {new Date(p.vigente_hasta)
                                      .toLocaleDateString('es-MX')}
                                  </span>
                                )}
                                <ChipEstado {...estadoHabilidad(p.estado)} />
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {vencimientos.length > 0 && (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <h2 className="font-semibold text-amber-900">
            Vencen pronto o ya vencieron
          </h2>
          <ul className="mt-2 flex flex-col gap-1 text-sm">
            {vencimientos.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3">
                <span className="text-amber-900">
                  {v.persona} · {v.habilidad}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-amber-800">
                    {new Date(v.vigente_hasta).toLocaleDateString('es-MX')}
                  </span>
                  <ChipEstado {...estadoHabilidad(v.estado)} />
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* LA ADVERTENCIA DE LA CONSTANCIA, SIEMPRE A LA VISTA mientras el
          formato no esté verificado. Quien va a emitir una tiene que
          saberlo antes de generarla, no al abrir el PDF. */}
      <p className="rounded-lg border border-red-300 bg-red-50 p-3
                    text-sm text-red-800">
        <strong>Sobre las constancias DC-3:</strong> {AVISO_DC3_SIN_VERIFICAR}
      </p>
    </div>
  );
}
